import { z } from 'zod';
import { PermissionStatusSchema, UnitAvailabilitySchema } from './enums.js';

// Public versioned event contracts (PRD §14–15). These are OUR proposed API, not provider schemas.

const PermissionClaim = z.object({
  status: PermissionStatusSchema,
  evidence_ref: z.string().max(200).optional(),
  captured_at: z.string().optional(),
});

export const IngressLeadDataSchema = z.object({
  contact: z.object({
    external_id: z.string().max(200).optional(),
    name: z.string().max(200).optional(),
    phone: z.string().max(40).optional(),
    email: z.string().max(254).optional(),
    timezone: z.string().max(64).optional(),
  }),
  /**
   * Acquisition data (spec §08.6). Only what was actually received is stored, append-only as a touchpoint; UTMs and
   * click ids missing here are read from `landing_page`'s query string when present. Never inferred.
   */
  source: z
    .object({
      system: z.string().max(60).optional(),
      /** One of TOUCH_CHANNELS; when absent the channel is derived from the other fields (never guessed from ids). */
      channel: z.string().max(40).optional(),
      source_detail: z.string().max(200).optional(),
      campaign_id: z.string().max(200).optional(),
      campaign_name: z.string().max(300).optional(),
      adset_id: z.string().max(200).optional(),
      adset_name: z.string().max(300).optional(),
      ad_id: z.string().max(200).optional(),
      ad_name: z.string().max(300).optional(),
      form_id: z.string().max(200).optional(),
      form_name: z.string().max(300).optional(),
      /** Meta Lead Ads lead id (leadgen_id). */
      lead_id: z.string().max(200).optional(),
      utm_source: z.string().max(300).optional(),
      utm_medium: z.string().max(300).optional(),
      utm_campaign: z.string().max(300).optional(),
      utm_term: z.string().max(300).optional(),
      utm_content: z.string().max(300).optional(),
      gclid: z.string().max(300).optional(),
      fbclid: z.string().max(300).optional(),
      wbraid: z.string().max(300).optional(),
      gbraid: z.string().max(300).optional(),
      ctwa_clid: z.string().max(300).optional(),
      landing_page: z.string().max(2000).optional(),
      referrer: z.string().max(2000).optional(),
      /** Click-to-WhatsApp referral object as sent by Meta (source_url, source_type, source_id, headline, body, ctwa_clid…). */
      referral: z.record(z.string(), z.unknown()).optional(),
    })
    .optional(),
  stage: z.string().max(80).optional(),
  /**
   * Form/ad answers (e.g. { budget: "4 MDP" }). Stored as DECLARED values: they personalize the conversation but never
   * count as confirmed for Calificado (§04.b, §15.6).
   */
  qualification: z.record(z.string().max(40), z.union([z.string().max(300), z.number()])).optional(),
  permissions: z.object({ whatsapp: PermissionClaim.optional(), phone: PermissionClaim.optional() }).optional(),
});

export const IngressInventoryDataSchema = z.object({
  unit_external_id: z.string().min(1).max(200),
  source_revision: z.union([z.number().int().nonnegative(), z.string().regex(/^\d+$/)]),
  changes: z.object({
    availability: UnitAvailabilitySchema.optional(),
    price_minor: z.union([z.number().int().nonnegative(), z.string().regex(/^\d+$/)]).optional(),
    currency: z.string().length(3).optional(),
    reservation_external_id: z.string().max(200).nullable().optional(),
    hold_expires_at: z.string().nullable().optional(),
  }),
});

export const IngressEventSchema = z.object({
  schema_version: z.literal('1.0'),
  event_id: z.string().min(1).max(200),
  type: z.string().min(1).max(80),
  occurred_at: z.string(),
  project_external_id: z.string().max(200).optional(),
  data: z.record(z.string(), z.unknown()),
});
export type IngressEvent = z.infer<typeof IngressEventSchema>;

export const IngressReceiptSchema = z.object({
  receipt_id: z.string(),
  event_id: z.string(),
  status: z.string(),
  duplicate: z.boolean(),
});
export type IngressReceipt = z.infer<typeof IngressReceiptSchema>;

/** Outgoing webhook envelope. event_id is stable across retries; delivery id changes per attempt. */
export const OutgoingEventSchema = z.object({
  schema_version: z.literal('1.0'),
  event_id: z.string(),
  type: z.string(),
  occurred_at: z.string(),
  workspace_id: z.string(),
  project_id: z.string().nullable(),
  correlation_id: z.string().nullable(),
  data: z.record(z.string(), z.unknown()),
});
export type OutgoingEvent = z.infer<typeof OutgoingEventSchema>;

/** Webhook signing headers (PRD §15). Signature = hex HMAC-SHA256 of `${timestamp}.${rawBody}`. */
export const WEBHOOK_HEADERS = {
  eventId: 'X-Event-Id',
  deliveryId: 'X-Delivery-Id',
  timestamp: 'X-Timestamp',
  signature: 'X-Signature',
} as const;

/** Retry schedule for outgoing deliveries (seconds): 1m, 5m, 30m, 2h, 8h, 24h, then dead-letter. */
export const WEBHOOK_RETRY_SECONDS = [60, 300, 1800, 7200, 28800, 86400] as const;
